import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Trash2, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useVoiceWebhookEvents } from '../api';
import { bodyTableToWire } from '../payload';
import type { VoiceBodyRow } from '../payload';
import { CustomBodyParamModal } from '../components/CustomBodyParamModal';

export interface VoiceWebhookFormValues {
  webhookName: string;
  url: string;
  event: string;
  markAsDefault: 0 | 1;
  authToken: string;
  headers: Record<string, string>;
  bodyTable: VoiceBodyRow[];
}

interface VoiceWebhookFormProps {
  mode: 'create' | 'update';
  initialValues: VoiceWebhookFormValues;
  isPending: boolean;
  onSubmit: (payload: {
    webhookName: string;
    url: string;
    event: string;
    markAsDefault: 0 | 1;
    authToken?: string;
    headerJson: string;
    bodyJson: string;
  }) => void;
}

export function VoiceWebhookForm({
  mode,
  initialValues,
  isPending,
  onSubmit,
}: VoiceWebhookFormProps) {
  const events = useVoiceWebhookEvents();

  const [webhookName, setWebhookName] = useState(initialValues.webhookName);
  const [url, setUrl] = useState(initialValues.url);
  const [event, setEvent] = useState(initialValues.event);
  const [markAsDefault, setMarkAsDefault] = useState<0 | 1>(initialValues.markAsDefault);
  const [authToken, setAuthToken] = useState(initialValues.authToken);
  const [headers, setHeaders] = useState<Record<string, string>>(initialValues.headers);
  const [bodyTable, setBodyTable] = useState<VoiceBodyRow[]>(initialValues.bodyTable);

  const [hdrParam, setHdrParam] = useState('');
  const [hdrValue, setHdrValue] = useState('');
  const [showBodyModal, setShowBodyModal] = useState(false);

  const addHeader = () => {
    const k = hdrParam.trim();
    if (!k) return toast.error('Header name is required.');
    if (/\s/.test(k)) return toast.error('Header name cannot contain spaces.');
    if (Object.keys(headers).some((existing) => existing.toLowerCase() === k.toLowerCase())) {
      return toast.error('That header is already set.');
    }
    setHeaders({ ...headers, [k]: hdrValue.trim() || k });
    setHdrParam('');
    setHdrValue('');
  };

  const removeHeader = (k: string) => {
    const next = { ...headers };
    delete next[k];
    setHeaders(next);
  };

  const toggleBodyRow = (id: number) => {
    setBodyTable((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)),
    );
  };

  const toggleBodyAll = () => {
    const allSelected = bodyTable.every((r) => r.selected);
    setBodyTable((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));
  };

  const editBodyParameter = (id: number, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return toast.error('Parameter name cannot be empty.');
    setBodyTable((prev) =>
      prev.map((r) => (r.id === id ? { ...r, parameter: trimmed } : r)),
    );
  };

  const appendCustomRows = (rows: VoiceBodyRow[]) => {
    if (rows.length === 0) return;
    // Re-number to keep ids contiguous with the existing table.
    setBodyTable((prev) => {
      let nextId = prev.length + 1;
      const renumbered = rows.map((r) => ({ ...r, id: nextId++ }));
      return [...prev, ...renumbered];
    });
  };

  const removeCustomBodyRow = (id: number) => {
    setBodyTable((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!webhookName.trim()) return toast.error('Webhook name is required.');
    if (!url.trim()) return toast.error('Webhook URL is required.');
    if (!event) return toast.error('Pick an event.');

    onSubmit({
      webhookName: webhookName.trim(),
      url: url.trim(),
      event,
      markAsDefault,
      authToken: authToken.trim() || undefined,
      headerJson: JSON.stringify(headers),
      bodyJson: JSON.stringify(bodyTableToWire(bodyTable)),
    });
  };

  const hasHeaders = Object.keys(headers).length > 0;
  const allBodySelected = bodyTable.length > 0 && bodyTable.every((r) => r.selected);
  const someBodySelected = bodyTable.some((r) => r.selected) && !allBodySelected;

  const headersPreview = JSON.stringify(headers, null, 2);
  const bodyPreview = JSON.stringify(bodyTableToWire(bodyTable), null, 2);

  return (
    <ScrollPage>
      <PageHeader
        title={mode === 'create' ? 'Add CDR Webhook' : 'Update CDR Webhook'}
        description="Define what the platform POSTs to your endpoint when a CDR event fires."
        actions={
          <Button asChild variant="outline">
            <Link to="/webhook-list">
              <ArrowLeft className="h-4 w-4" />
              Back to webhooks
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={webhookName}
                onChange={(e) => setWebhookName(e.target.value)}
                autoFocus={mode === 'create'}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/webhook"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event">Event</Label>
              <Select value={event} onValueChange={setEvent}>
                <SelectTrigger id="event">
                  <SelectValue
                    placeholder={events.isLoading ? 'Loading…' : 'Select an event'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(events.data ?? []).map((e) => (
                    <SelectItem key={e.eventName} value={e.eventName}>
                      {e.eventName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-4">
              <Label htmlFor="auth-token">Auth Token (optional)</Label>
              <Input
                id="auth-token"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                placeholder="Bearer token or API key for webhook authentication"
              />
            </div>
            <div className="flex items-center gap-2 md:col-span-4">
              <Checkbox
                id="default"
                checked={markAsDefault === 1}
                onCheckedChange={(v) => setMarkAsDefault(v ? 1 : 0)}
              />
              <Label htmlFor="default" className="cursor-pointer">
                Mark as default for this event
              </Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Headers</CardTitle>
            <CardDescription>
              HTTP headers sent with every webhook delivery.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="hdr-name">Parameter</Label>
                <Input
                  id="hdr-name"
                  value={hdrParam}
                  onChange={(e) => setHdrParam(e.target.value)}
                  placeholder="X-API-Key"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hdr-value">Value</Label>
                <Input
                  id="hdr-value"
                  value={hdrValue}
                  onChange={(e) => setHdrValue(e.target.value)}
                  placeholder="abcdef…"
                />
              </div>
              <Button type="button" size="sm" onClick={addHeader}>
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Parameter</th>
                      <th className="px-3 py-2 text-left font-medium">Value</th>
                      <th className="w-16 px-3 py-2 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!hasHeaders ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-4 text-center text-muted-foreground"
                        >
                          No headers configured.
                        </td>
                      </tr>
                    ) : (
                      Object.entries(headers).map(([k, v]) => (
                        <tr key={k} className="border-t border-border">
                          <td className="px-3 py-2 font-mono text-xs">{k}</td>
                          <td className="break-all px-3 py-2 font-mono text-xs">{v}</td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => removeHeader(k)}
                              aria-label={`Remove ${k}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {hasHeaders && (
                <div className="space-y-1.5">
                  <Label>Headers JSON</Label>
                  <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                    {headersPreview}
                  </pre>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Body</CardTitle>
            <CardDescription>
              Pick standard parameters below, or open the parameter picker to add
              up to 30 reserved custom slots (custom / TTS / SMS, 10 each).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Button
                type="button"
                size="sm"
                onClick={() => setShowBodyModal(true)}
              >
                <Plus className="h-4 w-4" />
                Add custom parameter
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="w-12 px-3 py-2 text-left font-medium">
                        <Checkbox
                          checked={
                            allBodySelected ? true : someBodySelected ? 'indeterminate' : false
                          }
                          onCheckedChange={toggleBodyAll}
                          aria-label="Select all body params"
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Parameter</th>
                      <th className="px-3 py-2 text-left font-medium">Value</th>
                      <th className="w-16 px-3 py-2 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bodyTable.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-4 text-center text-muted-foreground"
                        >
                          No body parameters available.
                        </td>
                      </tr>
                    ) : (
                      bodyTable.map((r) => (
                        <tr key={r.id} className="border-t border-border">
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={r.selected}
                              onCheckedChange={() => toggleBodyRow(r.id)}
                              aria-label={`Toggle ${r.parameter}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={r.parameter}
                              onChange={(e) =>
                                editBodyParameter(r.id, e.target.value)
                              }
                              className="w-full bg-transparent font-mono text-xs outline-none focus:underline"
                            />
                          </td>
                          <td className="break-all px-3 py-2 font-mono text-xs">
                            {r.custom ? r.value : `{${r.value}}`}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {r.custom && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => removeCustomBodyRow(r.id)}
                                aria-label={`Remove ${r.parameter}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="space-y-1.5">
                <Label>Selected body JSON</Label>
                <pre className="max-h-[32rem] min-h-64 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                  {bodyPreview}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to="/webhook-list">Cancel</Link>
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Webhook className="h-4 w-4" />
            )}
            {mode === 'create' ? 'Add webhook' : 'Save changes'}
          </Button>
        </div>
      </form>

      <CustomBodyParamModal
        open={showBodyModal}
        onOpenChange={setShowBodyModal}
        bodyTable={bodyTable}
        onApply={appendCustomRows}
      />
    </ScrollPage>
  );
}
